import { Link } from "react-router-dom";
import { HeartHandshake, Leaf, ShieldCheck, Users } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { site } from "../config/site";

// TODO: remplacer par de vraies photos du chef et de l'équipe (avec l'accord du client)
const TEAM_IMG = "https://images.unsplash.com/photo-1651440204216-548382747b40?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const SPICES_IMG = "https://images.unsplash.com/photo-1590237581598-988d27565521?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const valeurs = [
  { icon: HeartHandshake, title: "Authenticité", text: "Des recettes tunisiennes traditionnelles, fidèles aux saveurs d'origine." },
  { icon: Leaf, title: "Produits frais", text: "Des ingrédients frais sélectionnés avec soin, une préparation maison." },
  { icon: ShieldCheck, title: "Hygiène HACCP", text: "Respect strict des normes d'hygiène et de sécurité alimentaire." },
  { icon: Users, title: "Convivialité", text: "Un accueil chaleureux et une écoute attentive, au restaurant comme en événementiel." },
];

export default function APropos() {
  return (
    <main id="contenu-principal">
      <Seo
        title="À propos — Notre histoire et nos valeurs | TN FOOD"
        description="TN FOOD, SAS immatriculée au RCS de Troyes, partage la cuisine tunisienne authentique à Sainte-Savine et accompagne vos événements dans l'Aube. Découvrez notre histoire et nos valeurs."
        path="/a-propos"
      />

      <section className="bg-bordeaux text-cream py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal>
            <h1 className="font-display text-4xl md:text-5xl font-bold">À propos de TN FOOD</h1>
            <p className="mt-4 text-lg text-cream/85 max-w-2xl">Une table tunisienne généreuse, au service de vos repas et de vos événements.</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <h2 className="font-display text-3xl font-semibold text-encre">Notre histoire</h2>
            <div className="mt-5 space-y-4 text-taupe leading-relaxed">
              <p>
                TN FOOD est né d'une envie simple : faire découvrir à Troyes et à l'Aube la cuisine tunisienne authentique, celle des grandes tablées familiales, des épices parfumées et des gestes transmis de génération en génération.
              </p>
              <p>
                Installés à Sainte-Savine, nous avons d'abord ouvert notre restaurant, puis développé un service traiteur pour accompagner les moments qui comptent : mariages, fiançailles, anniversaires, baptêmes et événements d'entreprise.
              </p>
            </div>
            <blockquote className="mt-8 border-l-4 border-saffron bg-cream rounded-r-2xl p-6" data-testid="mot-president">
              <p className="text-encre italic leading-relaxed font-display text-lg">
                « La cuisine tunisienne, c'est la générosité avant tout. Que ce soit pour un déjeuner entre collègues ou le plus beau jour de votre vie, nous mettons le même soin dans chaque plat et le même cœur dans chaque accueil. »
              </p>
              <footer className="mt-4 text-sm font-semibold text-terracotta">Ali NEJI, Président</footer>
            </blockquote>
          </Reveal>
          <Reveal delay={0.15}>
            <img src={SPICES_IMG} alt="Épices méditerranéennes utilisées en cuisine tunisienne" loading="lazy" width="900" height="600" className="rounded-2xl shadow-md w-full object-cover h-80" />
            <div className="mt-6 bg-white rounded-2xl border border-terracotta/10 shadow-sm p-6" data-testid="fiche-identite">
              <h3 className="font-display text-xl font-semibold text-encre mb-4">Fiche d'identité</h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {[
                  ["Dénomination", "TN FOOD"],
                  ["Forme juridique", "SAS"],
                  ["Capital social", site.capital],
                  ["Siège social", site.address.full],
                  ["RCS", site.rcs],
                  ["SIREN", site.siren],
                  ["SIRET", site.siret],
                  ["TVA intracommunautaire", site.tvaIntra],
                  ["Début d'activité", site.activityStart],
                  ["Exercice social", "Clôture au 31 décembre"],
                  ["Statuts modifiés le", "24/12/2025"],
                  ["Président", "Monsieur NEJI Ali"],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-taupe">{k}</dt>
                    <dd className="font-medium text-encre">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 md:py-24" data-testid="apropos-valeurs">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Reveal className="mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-encre">Nos valeurs et nos engagements</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {valeurs.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <article className="bg-white rounded-2xl p-6 border border-terracotta/10 shadow-sm h-full">
                  <v.icon className="h-8 w-8 text-terracotta" strokeWidth={1.5} aria-hidden="true" />
                  <h3 className="font-display text-xl font-semibold text-encre mt-4">{v.title}</h3>
                  <p className="mt-2 text-sm text-taupe leading-relaxed">{v.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="apropos-equipe">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl grid lg:grid-cols-2 gap-12 items-center">
          {/* TODO: remplacer par une vraie photo du chef et de l'équipe */}
          <Reveal>
            <img src={TEAM_IMG} alt="L'équipe TN FOOD en cuisine" loading="lazy" width="900" height="600" className="rounded-2xl shadow-md w-full object-cover h-96" />
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="font-display text-3xl font-semibold text-encre">L'équipe</h2>
            <p className="mt-5 text-taupe leading-relaxed">
              Derrière chaque couscous et chaque buffet, une équipe passionnée : en cuisine, nos chefs perpétuent les recettes traditionnelles tunisiennes ; en salle comme sur vos événements, notre personnel veille à un service attentionné.
            </p>
            <Link to="/contact#devis" data-testid="apropos-cta" className="mt-8 inline-block rounded-full bg-terracotta text-cream px-8 py-3.5 font-semibold transition-all duration-300 hover:bg-terracotta-dark hover:-translate-y-0.5">
              Confiez-nous votre événement
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
